import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Stack,
  Switch,
} from "@skailer-com/uikit";
import { getGDPRPlaceholders } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useDataProtectionHook from "@/hooks/use-data-protection.hook.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useDataProtectionSettingsSave } from "@/queries/use-data-protection.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiDataProtectionProviderSettings,
  ApiDataProtectionSettingsPayload,
  DataProtectionConfigureTab,
  DataProtectionTypes,
} from "@/types/data-protection.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface DataProtectionConfigureModalProps extends ModalProps {
  data_protection_type: DataProtectionTypes;
  tab: DataProtectionConfigureTab;
  settings?: ApiDataProtectionProviderSettings | null | undefined;
}

export function DataProtectionConfigureModal({
  data_protection_type,
  tab,
  settings,
  ...props
}: DataProtectionConfigureModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { getTitleByType } = useDataProtectionHook();

  const DataProtectionSchemaObject = z
    .object({
      data_protection_type: z.enum(DataProtectionTypes),
      privacy_policy_link: z.string().trim(),
      privacy_policy_text: z.string(),
      retention_period_days: z
        .number({ error: t("api_errors:must_integer") })
        .int(t("api_errors:must_integer"))
        .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
        .max(3650, t("api_errors:maximum_value_must", { maximum: 3650 })),
      email_template: z.string().nullable().optional(),
      calendar_full_names: z.boolean().nullable().optional(),
    })
    .superRefine((values, context) => {
      if (!values.privacy_policy_link && !values.privacy_policy_text.trim()) {
        context.addIssue({
          code: "custom",
          path: ["privacy_policy_link"],
          message: t("data_protection:privacy_policy_required"),
        });
      }
    });

  const [activeTabIndex, setActiveTabIndex] = useState(
    tab === DataProtectionConfigureTab.privacy_policy_text
      ? 2
      : tab === DataProtectionConfigureTab.email_template
        ? 1
        : 0
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(DataProtectionSchemaObject),
    defaultValues: {
      data_protection_type: DataProtectionTypes.gdpr,
      email_template: settings?.email_template ?? "",
      calendar_full_names: settings?.calendar_full_names ?? false,
      retention_period_days: settings?.retention_period_days ?? 0,
      privacy_policy_link: settings?.privacy_policy_link ?? "",
      privacy_policy_text: settings?.privacy_policy_text ?? "",
    },
  });

  const dataProtectionUpdate = useDataProtectionSettingsSave(control);

  const onSubmit = handleSubmit(
    async (schema) => {
      const settingsPayload: ApiDataProtectionSettingsPayload = {
        data_protection_type,
        enabled: true,
        email_template: schema.email_template ?? "",
        retention_period_days: schema.retention_period_days,
        privacy_policy_link: schema.privacy_policy_link,
        privacy_policy_text: schema.privacy_policy_text || null,
        calendar_full_names: !!schema.calendar_full_names,
      };

      try {
        await dataProtectionUpdate.mutateAsync(settingsPayload);

        analyticsService.trackEvent(analyticEvents.dataProtection.settingsEdited);

        close(id);
      } catch {
        return;
      }
    },
    (validationErrors) => {
      if (validationErrors.privacy_policy_link || validationErrors.retention_period_days) {
        setActiveTabIndex(0);
      }
    }
  );

  return (
    <Modal
      title={t("data_protection:settings_title", {
        type_title: getTitleByType(data_protection_type),
      })}
      withCloser={true}
      {...props}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />

          <Button
            type={"submit"}
            size={"lg"}
            isLoading={dataProtectionUpdate.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
          <Button
            variant={"plain"}
            size={"lg"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Tabs activeTab={activeTabIndex} onActiveTabChange={setActiveTabIndex}>
          <TabItem title={t("common:settings")}>
            <Stack className={"pt-6"} gap={"md"}>
              <Stack>
                <Input
                  type={"number"}
                  label={t("common:period")}
                  {...register("retention_period_days", {
                    setValueAs: (value) => (value === "" ? undefined : Number(value)),
                  })}
                  error={errors.retention_period_days?.message}
                />

                <Input
                  type={"text"}
                  label={t("data_protection:privacy_policy_link")}
                  {...register("privacy_policy_link")}
                  error={errors.privacy_policy_link?.message}
                />
              </Stack>

              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={!!value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    size={"md"}
                    title={t("data_protection:allow_sync_with_external_service")}
                    description={t("data_protection:allow_sync_with_external_service_description")}
                  />
                )}
                control={control}
                name={"calendar_full_names"}
              />
            </Stack>
          </TabItem>
          <TabItem title={t("core:email_template")}>
            <Stack className={"py-6 min-h-40"} gap={"lg"}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholderOptions={getGDPRPlaceholders(t)}
                    placeholder={t("core:email_enter_body")}
                    error={errors.email_template?.message}
                  />
                )}
                control={control}
                name={"email_template"}
              />
            </Stack>
          </TabItem>
          <TabItem title={t("data_protection:privacy_policy_text")}>
            <Stack className={"py-6 min-h-40"} gap={"lg"}>
              <Controller
                control={control}
                name="privacy_policy_text"
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value}
                    onChange={onChange}
                    placeholder={t("data_protection:privacy_policy_text_placeholder")}
                    error={errors.privacy_policy_text?.message}
                  />
                )}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
}
